import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { LeadResponse } from '@/actions/leads/updateLead';
import { UpdateLeadApiPayload } from '@/actions/leads/updateLead';
import { LeadProfileForm } from '@/components/forms/lead/LeadProfileForm';
import LeadProfileFormSchema from '@/components/forms/schemas/lead/LeadProfileFormSchema';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/ui/use-toast';
import { Lead } from '@/types/lead.type';
import { ApiResponse } from '@/types/response.type';

type LeadProfileTabProps = {
  lead: Lead;
  updateLead: (
    payload: UpdateLeadApiPayload
  ) => Promise<ApiResponse<LeadResponse> | undefined>;
  isUpdating: boolean;
};

const getLeadNameParts = (lead: Lead) => {
  if (lead.first_name || lead.last_name) {
    return {
      first_name: lead.first_name || '',
      last_name: lead.last_name || '',
    };
  }

  const [first_name = '', ...lastNameParts] = (lead.full_name || '')
    .trim()
    .split(/\s+/);
  return {
    first_name,
    last_name: lastNameParts.join(' '),
  };
};

const LeadProfileTab = ({
  lead,
  updateLead,
  isUpdating,
}: LeadProfileTabProps) => {
  const isProcessing = isUpdating;
  const nameParts = getLeadNameParts(lead);

  const form = useForm<z.infer<typeof LeadProfileFormSchema>>({
    resolver: zodResolver(LeadProfileFormSchema),
    defaultValues: {
      id: lead.id,
      first_name: nameParts.first_name,
      last_name: nameParts.last_name,
      email: lead.email || '',
      birth_date: lead.birth_date || '',
      city: lead.city || '',
      state: lead.state || '',
      zip_code: lead.zip_code || '',
      address: lead.address || '',
      phone: lead.phone || '',
      meeting_link: lead.meeting_link || '',
      linkedin: lead.linkedin || '',
      preferred_markets: lead.preferred_markets || [],
    },
  });

  useEffect(() => {
    if (lead) {
      const nextNameParts = getLeadNameParts(lead);

      form.reset({
        id: lead.id,
        first_name: nextNameParts.first_name,
        last_name: nextNameParts.last_name,
        email: lead.email || '',
        birth_date: lead.birth_date || '',
        city: lead.city || '',
        state: lead.state || '',
        zip_code: lead.zip_code || '',
        address: lead.address || '',
        phone: lead.phone || '',
        meeting_link: lead.meeting_link || '',
        linkedin: lead.linkedin || '',
        preferred_markets: lead.preferred_markets || [],
      });
    }
  }, [lead, form]);

  const handleSubmit = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const formData = form.getValues();

    try {
      await updateLead({
        leadId: lead.id,
        data: {
          first_name: formData.first_name?.trim() || undefined,
          last_name: formData.last_name?.trim() || undefined,
          email: formData.email,
          phone: formData.phone || undefined,
          birth_date: formData.birth_date || undefined,
          address: formData.address || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          zip_code: formData.zip_code || undefined,
          linkedin: formData.linkedin || undefined,
          meeting_link: formData.meeting_link || undefined,
          preferred_markets: formData.preferred_markets || undefined,
        },
      });
      toast({
        title: 'Success',
        description: 'Profile updated successfully',
      });
    } catch (error) {
      console.error('Error updating lead profile:', error);
      toast({
        title: 'Something went wrong',
        description: 'Failed to update profile. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="relative space-y-2">
      {isProcessing && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-100/50 backdrop-blur-sm">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}

      <LeadProfileForm form={form} />

      <div className="flex w-full items-center justify-end gap-2">
        <Button
          size="sm"
          className="w-full md:w-fit"
          onClick={handleSubmit}
          disabled={isProcessing}
        >
          Update Profile
        </Button>
      </div>
    </div>
  );
};

export default LeadProfileTab;
